/**
 * Creature registry — single source of truth.
 *
 * Lives in `shared/` so both server (gameplay) and client (visuals) read the
 * same row. Adding a new creature = adding one row here. If you find yourself
 * editing a second file that lists creature names, you've hit a leak — fix the
 * leak. See `docs/build/data-driven-features.md` for the pattern.
 *
 * Predicate-aware drop rolls (Ghoul vs bot killer; Bear LuckyFangs perk) live
 * server-side in `game/server/src/creatures/drop-rolls.ts` because they need
 * the bot/perk predicates that aren't shared.
 *
 * Phase 1: Stag, Sheep, Fox (passive prey).
 * Phase 2: Wolf, Bear, Lyote (wildlife predators) + Ghoul, UndeadKobold, Wight,
 *   Drekavac (undead camp) + KingFurbeastIV (descoped boss) + TheOldMammoth (landmark).
 *
 * Cite-first style: every formula coefficient maps to a MapScript.galaxy line.
 * If a number disagrees with `docs/design/10-creatures.md`, the spec
 * (`docs/superpowers/specs/2026-04-27-creatures-feature-design.md`) is canonical
 * because it includes the 2026-04-27 extract sweep.
 */
export type CreatureType =
  | "Stag" | "Sheep" | "Fox"        // Plan 1: passive prey
  | "Wolf" | "Bear" | "Lyote"       // Plan 2: wildlife predators
  | "Ghoul" | "UndeadKobold" | "Wight" | "Drekavac"  // Plan 2: undead
  | "KingFurbeastIV"                // Plan 2: boss
  | "TheOldMammoth"                 // Plan 2: landmark
  | "Nightwisp";                    // pets-only (Tavern purchase) — never pool-spawned

export type CreatureRole = "passive-flee" | "neutral-retaliate" | "hostile-hunt" | "boss" | "landmark";
export type DayNightActivity = "always" | "day-only" | "night-only";

/**
 * Undead creature types — the Plan-2 undead camp. These are NOT biological, so
 * biological-gated weapon DoTs (NoxiousVenom poison, VenomousArbalestPoison,
 * FelFlames) must NOT proc on them. Everything else (wildlife prey, predators,
 * boss, landmark, pets) is biological.
 *
 * NOTE: KTS `targeting.ts` treats every creature as a valid "Biological" spell
 * target (cubes + creatures), so the ability-targeting layer does NOT
 * distinguish undead. This set is the dedicated biological-ness source for the
 * item-proc DoT gate (combat-procs.ts) — there is no per-creature `biological`
 * flag on CreatureDef. If a clean flag is ever added, replace `isBiologicalCreature`
 * with a def lookup. */
export const UNDEAD_CREATURE_TYPES: ReadonlySet<CreatureType> = new Set<CreatureType>([
  "Ghoul",
  "UndeadKobold",
  "Wight",
  "Drekavac",
]);

/** True if a creatureType is biological (i.e. NOT undead). Drives the
    biological-DoT gate for weapon on-hit procs on creature targets. Unknown
    types default to biological (fail-open: a new wildlife type still takes
    poison). */
export function isBiologicalCreature(creatureType: string): boolean {
  return !UNDEAD_CREATURE_TYPES.has(creatureType as CreatureType);
}

export interface DropEntry {
  /** Item kind matching ResourceNode.kind or a future Pickup type. */
  kind: string;
  /** 0..1 probability gate. */
  chance: number;
  /** Count dropped if chance passes. KTS always 1; future creatures may differ. */
  count: number;
}

export interface WeaponProfile {
  /** WeaponData.xml id, for cite-trace. */
  id: string;
  /** Melee range in u (KTS WeaponData.xml Range). 0.25 for tiny critters; 0.5 for kobold-class. */
  range: number;
  /** Swing cooldown in seconds (Period). */
  periodSec: number;
  /** Wind-up before damage applies, seconds. KTS implicit 0.25 if unset. */
  damagePointSec: number;
}

/** Damage-response sprint behavior. Bear's AnimalRush (KTS-verbatim,
    BehaviorData.xml:7794-7801 + EffectData.xml:11139-11144): on TAKING damage,
    `chance` to gain `mult`× movespeed for rand(minSec, maxSec) seconds.
    NOT chase-triggered — a proc on the damage-response path. Generalized so
    future damage-rushers (Boar, ...) are a registry edit, not a code edit in
    `neutral-retaliate.ts`. */
export interface DamageRushProfile {
  /** 0..1 probability the rush procs on each damage instance taken. */
  chance: number;
  /** Movespeed multiplier while active. */
  mult: number;
  /** Duration roll bounds in seconds — rand(minSec, maxSec) per proc. */
  minSec: number;
  maxSec: number;
}

/** Night-aggro behavior (Wolf). KTS "Beast" behavior: periodic search ordering
    attacks on kobolds within `acquireRadiusU` (EffectData.xml:6808-6820, 4.5u).
    Active only while phase = night; day posture stays neutral-retaliate.
    Fire repulsion is layered server-side in `wolf-night.ts` (absent from KTS
    data — implemented from the player-facing spec). */
export interface NightAggroProfile {
  /** Acquire radius in u for the periodic night scan. */
  acquireRadiusU: number;
}

export interface CreatureDef {
  type: CreatureType;
  /** Three.js asset path; client uses this verbatim. `null` for creatures with
      no GLTF mesh (e.g. orb-rendered Nightwisp) — the client preloader skips
      null and the renderer falls through to its procedural-orb path. */
  meshUrl: string | null;
  /** Visual scale multiplier applied to the loaded mesh. World convention:
      1u = 1m. Real-world body-height calibrated; see commit history for the
      Quaternius-pack native sizes that informed each value. */
  scale: number;
  role: CreatureRole;
  activity: DayNightActivity;
  /** SC2 unit speed (UnitData.xml). World convention: 1 unit = 1 meter, so this
      is u/sec at full sprint multiplier 1.0. Sprint multipliers (e.g., 3× from
      StagSprint) are layered in passive-flee.ts; this is the base. */
  baseSpeed: number;
  /** Per-creature region pool index (gv_unitRegion[N]). Used by spawner for
      pool/quota lookup. Does NOT key the region polygon — that's the 4 map quarters,
      shared by every wildlife/undead pool. KTS keeps this indirection so the
      spawner can have per-pool quota math. Negative => not pool-spawned (boss/landmark). */
  poolIndex: number;
  /** SC2 sight (UnitData.xml). 0 = no auto-acquire; passive-prey relies on
      the 9u alarm-radius scan in passive-flee.ts. */
  sight: number;
  /** XP awarded to the killer; verbatim from death handlers in MapScript.galaxy. */
  xp: number;
  /** True for Stag-class flee behavior: 1s proximity scan + StagSprint (3×) + stamina counter.
      False for Pheasant/Sheep-class: damage-only flee, no sprint, no stamina. */
  proximityFlee: boolean;
  /** Minimum seconds between spawn attempts of this type. Default 5 (matches
      gf_SpawnResources tick body). Stag uses 65 (KTS dynamicRareSpawn cadence). */
  spawnIntervalSec: number;
  /** Max units spawned per spawn tick. Default 5 (matches KTS clamp 1..5).
      Stag uses 1 (KTS dynamicRareSpawn is single-spawn). */
  spawnBurstMax: number;
  /** Drop table; verbatim from MapScript.galaxy:29176-29788 per-creature. */
  drops: ReadonlyArray<DropEntry>;
  /** Time-scaled max-HP formula. gameDurationSec is match elapsed seconds.
      Returns LifeMax post-CatalogFieldValueSet (gf_SetScaledStats). */
  scaleHp(gameDurationSec: number): number;
  /** Optional weapon profile. Absent for passive prey, landmark, and the
      descoped KingFurbeastIV boss (no melee per spec §"King Furbeast IV" Plan 2 descope). */
  weapon?: WeaponProfile;
  /** Time-scaled damage formula. Returns post-scaleStats Damage Amount.
      Absent if `weapon` is absent. KTS-verbatim formulas in MapScript.galaxy:9651 block. */
  scaleDamage?: (gameDurationSec: number) => number;
  /** True if the creature participates in `flock-behavior.ts` (FlockBehaviorSheep
      port). Currently only Sheep. Generalizable: a future Cattle / Pheasant could
      flip this on without code changes. */
  flock?: boolean;
  /** Damage-response sprint (Bear AnimalRush). Proc-rolled on neutral-retaliate
      damage; the swing-target lock is independent (handled by the role). */
  damageRush?: DamageRushProfile;
  /** Night-phase aggro-on-sight (Wolf). Server gates the periodic acquire scan
      on this flag in `wolf-night.ts`; absent = no night aggro. */
  nightAggro?: NightAggroProfile;
  /** Removed (not killed — no drops/XP) on the night→day transition. KTS
      gf_MorningHappened removes Lyote + Nightcrawler (MS:18986/19036).
      Lyote in v1; Nightcrawler joins via this flag if it ever ships. */
  dawnCull?: boolean;
  /** KTS DynamicRareSpawnDistributionAttempt (MapScript.galaxy:19740): spawn
      rand(minU, maxU) from a RANDOM living player instead of uniform-random in
      a map quarter. Spawner falls back to uniform placement when no valid
      point / no players. Stag + Bear. */
  nearPlayerSpawn?: { minU: number; maxU: number };
  /** SC2 `Armored` attribute (UnitData.xml). v1 players are `Biological`-only, so
      this only matters for Forestry L4 "Forestry Heal" (20% lifesteal vs Armored
      or Structure on melee — Decision M). Bear + boss carry it; wildlife prey and
      undead do not. Absent = not armored. */
  armored?: boolean;
  /** Effective body radius (u) for melee APPROACH only. A melee attacker stops at
      RANGE + bodyRadius from the creature's centre so it doesn't path into a big
      creature's torso and overlap it (playtest 2026-06-12: "bear is too close").
      Absent / 0 = kobold-sized; the flat RANGE is fine. Set only for creatures
      whose mesh is visibly larger than a kobold. Tune against the rendered model. */
  bodyRadius?: number;
}

/**
 * Stag — Day-active passive prey.
 *   UnitData.xml:6428–6443 (Speed=1.25, Sight=0)
 *   gf_SetScaledStats: LifeMax = 70 + gameDuration / 20
 *   gf_StagDeath (MapScript.galaxy:29373–29397): unconditional UnitCreate for
 *   RawStagMeat + Leather + Antlers inside owner guard (100% effective) + 100 XP.
 *   pool index 6 (gv_unitRegion[6])
 *   Cadence: 1 spawn per 5s tick up to quota 21 (MS:20783). The 65s trigger
 *   (MS:41307) is a spatial-distribution reset, NOT cadence — earlier port
 *   misread it as spawnIntervalSec 65 (fixed in the 2026-06-10 tuning sweep).
 *   Placement: 11-15u from a random player (DynamicRareSpawnDistributionAttempt,
 *   MS:19740) via `nearPlayerSpawn`.
 *   Mesh scale 0.33 → ~1.3m red-deer shoulder (Quaternius Stag.gltf native 3.894u).
 */
const STAG: CreatureDef = {
  type: "Stag",
  meshUrl: "/assets/animals/Stag.gltf",
  scale: 0.33,
  role: "passive-flee",
  activity: "day-only",
  baseSpeed: 1.25,
  poolIndex: 6,
  sight: 0,
  xp: 100,
  proximityFlee: true,
  spawnIntervalSec: 5,
  spawnBurstMax: 1,
  nearPlayerSpawn: { minU: 11, maxU: 15 },
  drops: [
    { kind: "RawStagMeat", chance: 1.0, count: 1 },
    { kind: "Leather",     chance: 1.0, count: 1 },
    { kind: "Antlers",     chance: 1.0, count: 1 },
  ],
  scaleHp: (g) => 70 + g / 20,
};

/**
 * Sheep — Always-active passive prey.
 *   UnitData.xml:5062 (SheepFixed — the variant KTS spawns; Speed inherits SC2
 *   Critter default 2.25, no map override; Sight=0)
 *   gf_SetScaledStats: static 40 (SheepFixed; no scaling)
 *   gf_SheepDeath (MapScript.galaxy:29176–29198): RawLamb 100% + Wool 50% + 35 XP wild.
 *   pool index 1 (gv_unitRegion[1])
 *   Mesh scale 0.22 → ~0.8m sheep shoulder (Cow stand-in placeholder).
 *   `flock: true` — only flocking creature in v1; FlockBehaviorSheep port lives
 *   in `server/creatures/flock-behavior.ts` (gates on this flag).
 */
const SHEEP: CreatureDef = {
  type: "Sheep",
  // Prehistoric re-theme: herd-prey slot now renders an Ancient Bison (polyperfect, animated).
  meshUrl: "/assets/animals/AncientBison.glb",
  scale: 0.85, // TUNE in QA — native ~2.17m, trimmed toward bison shoulder height

  role: "passive-flee",
  activity: "always",
  baseSpeed: 2.25,
  poolIndex: 1,
  sight: 0,
  xp: 35,
  proximityFlee: false,
  spawnIntervalSec: 5,
  spawnBurstMax: 5,
  drops: [
    { kind: "RawLamb", chance: 1.0, count: 1 },
    { kind: "Wool",    chance: 0.5, count: 1 },
  ],
  scaleHp: () => 40,
  flock: true,
};

/**
 * Fox — Always-active passive prey. v1 mesh-reskin of KTS Pheasant
 * (no bird mesh in available asset packs; design call 2026-04-27).
 *   UnitData.xml:8688 (Speed inherits SC2 Critter default 2.25, Sight=0)
 *   gf_SetScaledStats: static 15 (no scaling)
 *   gf_PheasantDeath (MapScript.galaxy:29351–29368): RawFox 100% + 30 XP.
 *   pool index 7 (KTS gv_unitRegion[7], variable name: gv_blueJunglefowlQuota)
 *   Mesh scale 0.34 → ~0.64m red-fox shoulder (Quaternius Fox.gltf native 1.885u).
 *   Drop renamed RawPheasant → RawFox for Viking re-theme (2026-04-27 design call);
 *   no RawFox resource type exists in 06-economy-gear.md yet — drops.ts will
 *   spawn it as a generic ResourceNode pickup.
 */
const FOX: CreatureDef = {
  type: "Fox",
  meshUrl: "/assets/animals/Fox.gltf",
  scale: 0.34,
  role: "passive-flee",
  activity: "always",
  baseSpeed: 2.25,
  poolIndex: 7,
  sight: 0,
  xp: 30,
  proximityFlee: false,
  spawnIntervalSec: 5,
  spawnBurstMax: 5,
  drops: [
    { kind: "RawFox", chance: 1.0, count: 1 },
  ],
  scaleHp: () => 15,
};

// === Plan 2: wildlife predators ===

/**
 * Wolf — Always-active, phase-switched behavior (2026-06-10 ruling).
 *   UnitData.xml:4520-4535 (Speed=2.25, Sight=0)
 *   KTS spawns wolves day AND night (MS:20725-20735; gf_MorningHappened
 *   MS:18986 removes only Lyote + Nightcrawler) — earlier "night-only" was a
 *   corpus error.
 *   DAY: passive, retaliate-only (the neutral-retaliate posture).
 *   NIGHT: aggro players on sight — KTS "Beast" periodic search orders attacks
 *   on kobolds within 4.5u (EffectData.xml:6808-6820) → `nightAggro`.
 *   Night fire repulsion is server-side spec-driven (wolf-night.ts); absent
 *   from KTS data.
 *   gf_SetScaledStats: LifeMax = 40 + g/36; WolfBiteDamage = 10 + g/180
 *   gf_WolfDeath (MapScript.galaxy:29207-29225): Leather 100% + RawWolfMeat 100% + 40 XP.
 *   pool index 2 (gv_unitRegion[2])
 *   Mesh scale 0.30 → ~0.85m gray-wolf shoulder.
 */
const WOLF: CreatureDef = {
  type: "Wolf",
  // Prehistoric re-theme: Wolf slot now renders a Sabertooth (polyperfect, animated).
  meshUrl: "/assets/animals/Sabertooth.glb",
  scale: 1.33, // 2/3 of 2× (Aviv 2026-06-23: 2× was too big)

  role: "neutral-retaliate",
  activity: "always",
  baseSpeed: 2.25,
  poolIndex: 2,
  sight: 0,
  xp: 40,
  proximityFlee: false,
  spawnIntervalSec: 5,
  spawnBurstMax: 5,
  drops: [
    { kind: "Leather",     chance: 1.0, count: 1 },
    { kind: "RawWolfMeat", chance: 1.0, count: 1 },
  ],
  scaleHp: (g) => 40 + g / 36,
  weapon: { id: "WolfBite", range: 0.5, periodSec: 1.0, damagePointSec: 0.25 },
  scaleDamage: (g) => 10 + g / 180,
  nightAggro: { acquireRadiusU: 4.5 },
};

/**
 * Bear — Always-active neutral-retaliate. AnimalRush proc on damage taken.
 *   UnitData.xml:6638 (Speed inherits SC2 Critter default 2.25, Sight=0)
 *   gf_SetScaledStats: LifeMax = 150 + g/18; BearMaulDamage = 20 + g/90
 *   gf_BearDeath (MapScript.galaxy:29302-29336): BearPelt 100% + RawBearMeat 100% + WhiteBearFang 5%
 *     (20% if killer has LuckyFangs perk — predicate ships returning false until expertise re-port).
 *   pool index 4 (gv_unitRegion[4])
 *   Placement: 11-15u from a random player (DynamicRareSpawnDistributionAttempt,
 *   MS:19740) via `nearPlayerSpawn`.
 *   v1 mesh: Bull stand-in (placeholder per BUILD.md). Scale 0.40 → ~1.2m brown-bear shoulder.
 *   `damageRush: { chance: 0.25, mult: 2, minSec: 2, maxSec: 5 }` — KTS-verbatim
 *   AnimalRush (BehaviorData.xml:7794-7801, EffectData.xml:11139-11144): 25%
 *   chance ON TAKING DAMAGE to gain 2× move speed for rand(2,5)s. Net: base
 *   2.25 vs player 2.5 (escapable); a rushing bear is 4.5 (not escapable).
 */
const BEAR: CreatureDef = {
  type: "Bear",
  // Prehistoric re-theme: real animated Cave Bear (short-faced bear, polyperfect) —
  // fixes the long-standing bull-mesh-as-bear bug.
  meshUrl: "/assets/animals/ShortFacedBear.glb",
  scale: 1.7, // 1.7× (Aviv 2026-06-25: 85% of prior 2× — bears were oversized)

  role: "neutral-retaliate",
  activity: "always",
  baseSpeed: 2.25,
  poolIndex: 4,
  sight: 0,
  xp: 100,
  proximityFlee: false,
  spawnIntervalSec: 5,
  // Bears are a high-value "get" and must stay SPREAD OUT, not clumped.
  // Root cause of clumping: bears use near-player placement (nearPlayerSpawn 11-15u);
  // a burst of 5 per tick packs several bears into a tight ring around ONE randomly
  // picked player. Capping the burst to 1 (matching Stag's rare-spawn cadence) fills
  // quota 18 gradually, each near a freshly re-picked player — no clump. (Aviv 2026-06-25)
  spawnBurstMax: 1,
  drops: [
    { kind: "BearPelt",       chance: 1.0,  count: 1 },
    { kind: "RawBearMeat",    chance: 1.0,  count: 1 },
    { kind: "WhiteBearFang",  chance: 0.05, count: 1 },  // perk-aware bump applied in rollDrops
  ],
  scaleHp: (g) => 150 + g / 18,
  weapon: { id: "BearMaul", range: 0.5, periodSec: 1.5, damagePointSec: 0.25 },
  scaleDamage: (g) => 20 + g / 90,
  damageRush: { chance: 0.25, mult: 2, minSec: 2, maxSec: 5 },
  nearPlayerSpawn: { minU: 11, maxU: 15 },
  armored: true, // Forestry Heal trigger (Decision M)
  bodyRadius: 0.9, // wide quadruped — attacker stops back so it doesn't stand in the torso
};

/**
 * Lyote — Night-active hostile-hunt (HuntKobolds 1s random-pick).
 *   Dawn cull: all living Lyotes are removed on the night→day transition
 *   (KTS gf_MorningHappened, MS:19036) — wired in HelloRoom's DAWN handler.
 *   UnitData.xml:4650-4660 (Speed=2.25, Sight=0)
 *   gf_SetScaledStats: LifeMax = 50 + g/30; DirewolfBiteDamage = 15 + g/120
 *   gf_LyoteDeath (MapScript.galaxy:29254-29275): RawWolfMeat 100% + Leather 50% + 50 XP.
 *   (BoneshardBlade-conditional 100% Leather branch ignored — item not in v1 starter loop.)
 *   pool index 3 (gv_unitRegion[3])
 *   v1 mesh: Husky (Quaternius Animals). Scale 0.25 → ~0.65m.
 */
const LYOTE: CreatureDef = {
  type: "Lyote",
  // Prehistoric re-theme: Lyote ("Direwolf") now renders the polyperfect DireWolf.
  meshUrl: "/assets/animals/DireWolf.glb",
  scale: 1.0, // TUNE in QA

  role: "hostile-hunt",
  activity: "night-only",
  baseSpeed: 2.25,
  poolIndex: 3,
  sight: 0,
  xp: 50,
  proximityFlee: false,
  spawnIntervalSec: 5,
  spawnBurstMax: 5,
  drops: [
    { kind: "RawWolfMeat", chance: 1.0, count: 1 },
    { kind: "Leather",     chance: 0.5, count: 1 },
  ],
  scaleHp: (g) => 50 + g / 30,
  weapon: { id: "DirewolfBite", range: 0.5, periodSec: 1.0, damagePointSec: 0.25 },
  scaleDamage: (g) => 15 + g / 120,
  dawnCull: true,
};

// === Plan 2: undead camp ===

/**
 * Ghoul — hostile-hunt.
 *   UnitData.xml:30-45 (Speed=1.0, Sight=6)
 *   gf_SetScaledStats: LifeMax = 25 + g*0.015; GhoulAttackDamage = 6 + g*0.005
 *   gf_GhoulDeath (MapScript.galaxy:29406-29427): SkeletalFinger 10% (15% if killer is bot kobold) + 10 XP.
 *   pool index 14 (shared by all undead). Scale 0.55 → ~1.7m humanoid (Zombie_Male).
 */
const GHOUL: CreatureDef = {
  type: "Ghoul",
  meshUrl: "/assets/creatures/Ghoul.gltf",
  scale: 0.55,
  role: "hostile-hunt",
  activity: "always",
  baseSpeed: 1.0,
  poolIndex: 14,
  sight: 6,
  xp: 10,
  proximityFlee: false,
  spawnIntervalSec: 2.5,
  spawnBurstMax: 5,
  drops: [
    { kind: "SkeletalFinger", chance: 0.10, count: 1 },  // bumped to 0.15 if killer is bot kobold (rollDrops)
    { kind: "BoneTalisman",   chance: 0.10, count: 1 },  // trophy item → Hunter's Lodge deposit → +10 pet HP
  ],
  scaleHp: (g) => 25 + g * 0.015,
  weapon: { id: "GhoulClaws", range: 0.25, periodSec: 1.25, damagePointSec: 0.2666 },
  scaleDamage: (g) => 6 + g * 0.005,
};

/**
 * UndeadKobold — hostile-hunt.
 *   UnitData.xml:2584-2598 (Speed=1.20, Sight=6)
 *   gf_SetScaledStats: LifeMax = 35 + g*0.08; UndeadKoboldAttackDamage = 6 + g*0.02
 *   gf_UndeadKoboldDeath (MapScript.galaxy:29437-29449): nothing (CoinPurse 5-10g commented-out in KTS) + 50 XP.
 *   pool index 14. Scale 0.45 → ~1.4m (Wizard stand-in, smaller than human).
 */
const UNDEAD_KOBOLD: CreatureDef = {
  type: "UndeadKobold",
  meshUrl: "/assets/creatures/UndeadKobold.gltf",
  scale: 0.45,
  role: "hostile-hunt",
  activity: "always",
  baseSpeed: 1.20,
  poolIndex: 14,
  sight: 6,
  xp: 50,
  proximityFlee: false,
  spawnIntervalSec: 2.5,
  spawnBurstMax: 5,
  drops: [],
  scaleHp: (g) => 35 + g * 0.08,
  weapon: { id: "DecayingBash", range: 0.25, periodSec: 0.9, damagePointSec: 0.2666 },
  scaleDamage: (g) => 6 + g * 0.02,
};

/**
 * Wight — hostile-hunt.
 *   UnitData.xml:1587-1601 (Speed=1.5, Sight=6)
 *   gf_SetScaledStats: LifeMax = 35 + g*0.08; WightRendingClawsDamage = 6 + g/40
 *   gf_WightDeath (MapScript.galaxy:29457-29470): SkeletalFinger 10% + 50 XP.
 *   pool index 14. Scale 0.55 → ~1.7m (Zombie_Female).
 */
const WIGHT: CreatureDef = {
  type: "Wight",
  meshUrl: "/assets/creatures/Wight.gltf",
  scale: 0.55,
  role: "hostile-hunt",
  activity: "always",
  baseSpeed: 1.5,
  poolIndex: 14,
  sight: 6,
  xp: 50,
  proximityFlee: false,
  spawnIntervalSec: 2.5,
  spawnBurstMax: 5,
  drops: [
    { kind: "SkeletalFinger", chance: 0.10, count: 1 },
  ],
  scaleHp: (g) => 35 + g * 0.08,
  weapon: { id: "WightRendingClaws", range: 0.25, periodSec: 1.1, damagePointSec: 0.5 },
  scaleDamage: (g) => 6 + g / 40,
};

/**
 * Drekavac — hostile-hunt (HuntKobolds2 — identical shape, different periodic Behavior id).
 *   UnitData.xml:1794-1809 (Speed=2.85, Sight=6)
 *   gf_SetScaledStats: LifeMax = 40 + g*0.10; DrekavacAttackDamage = 8 + g/40
 *   gf_DrekavacDeath (MapScript.galaxy:29479-29488): nothing + 70 XP.
 *   pool index 14. Scale 0.55 → ~1.7m spectral (Witch stand-in).
 */
const DREKAVAC: CreatureDef = {
  type: "Drekavac",
  meshUrl: "/assets/creatures/Drekavac.gltf",
  scale: 0.55,
  role: "hostile-hunt",
  activity: "always",
  baseSpeed: 2.85,
  poolIndex: 14,
  sight: 6,
  xp: 70,
  proximityFlee: false,
  spawnIntervalSec: 2.5,
  spawnBurstMax: 5,
  drops: [],
  scaleHp: (g) => 40 + g * 0.10,
  weapon: { id: "DrekavacTalons", range: 0.25, periodSec: 0.8, damagePointSec: 0.4 },
  scaleDamage: (g) => 8 + g / 40,
};

// === Plan 2: boss + landmark ===

/**
 * KingFurbeastIV — boss. Spawn-on-match-start; no autonomous hunting.
 *   UnitData.xml:6967-6980 (Speed=1.75, Sight=6 / dusk=2)
 *   gf_SetScaledStats: LifeMax = (280 + g/5) * 1.0; LifeRegen / Armor scale also (currently unused —
 *     Cube.armor field exists but Creature schema has no armor field; deferred until armor matters).
 *   Scale 0.80 → ~2.5m boss silhouette (Golden Knight stand-in).
 *
 * v1 DESCOPE (spec §7-10, locked 2026-04-28): no offensive ability (TheKingsSpeech cut),
 * no melee weapon, no respawn. Boss is a stationary HP sponge that awards +200 XP per
 * attacker via the standard combat.kill hook on death.
 */
const KING_FURBEAST_IV: CreatureDef = {
  type: "KingFurbeastIV",
  meshUrl: "/assets/creatures/KingFurbeast.gltf",
  scale: 0.80,
  role: "boss",
  activity: "always",
  baseSpeed: 1.75,
  poolIndex: -1,         // not in spawner pool — spawned by boss.ts
  sight: 6,
  xp: 200,
  proximityFlee: false,
  spawnIntervalSec: Number.POSITIVE_INFINITY,  // spawner ignores
  spawnBurstMax: 0,
  drops: [],             // TreasureChest cut for v1 (spec §9). +200 XP only.
  scaleHp: (g) => (280 + g / 5) * 1.0,
  // weapon + scaleDamage intentionally absent (descope per spec §8).
  armored: true, // Forestry Heal trigger (Decision M) — the boss is a sustain target
  bodyRadius: 1.6, // ~2.5m boss silhouette — keep melee attackers off the model
};

/**
 * TheOldMammoth — landmark. Static placement at (77.0, 41.5) per 03-world.md; inert.
 *   UnitData.xml:9135 (LifeMax=200, no weapons, no behaviors)
 *   gf_MammothDeath (MapScript.galaxy:29770-29790): KTS gives summer-only BrimmingHeart +
 *     BrimmingEssence. Seasons cut → no v1 drops. +200 XP if it ever dies.
 *   Scale 0.75 → ~3m (Bull stand-in).
 */
const THE_OLD_MAMMOTH: CreatureDef = {
  type: "TheOldMammoth",
  // Prehistoric re-theme: real animated Columbian Mammoth (polyperfect).
  meshUrl: "/assets/animals/ColumbianMammoth.glb",
  scale: 0.83, // 3× smaller than 2.5 (Aviv 2026-06-23: mammoth was too big)

  // Prehistoric re-theme (Aviv 2026-06-23): mammoth promoted from inert landmark to a
  // ROAMING wild megafauna. flock:true is reused purely as the codebase's only wander AI —
  // flock-behavior.ts walks them toward herdmates / random points and sets aiState "wander"
  // (verified live 2026-06-23: ~12-14u displacement / 18s). At quota 6 over a 254² map they
  // roam SOLO and don't visibly cluster into a herd — a tighter spawn anchor or stronger
  // cohesion would be needed for that. passive-flee + proximityFlee:false = only flees when
  // attacked. Tamable-apex behaviour — the mammoth REPLACES the (never-built) frost-bear apex
  // concept — is a later gameplay slice (TODO; see QUEUE.md). This slice is mesh + roaming.
  role: "passive-flee",
  activity: "always",
  baseSpeed: 1.0,        // slow, lumbering
  poolIndex: 5,          // previously -1 (landmark); now an ambient wildlife bucket
  sight: 0,
  xp: 200,
  proximityFlee: false,
  spawnIntervalSec: 8,
  spawnBurstMax: 1,
  flock: true,           // reuse flock-behavior as the wander engine (roams; doesn't visibly herd at this density)
  drops: [
    { kind: "Leather", chance: 1.0, count: 3 }, // placeholder; refine in the drop-rename slice
  ],
  scaleHp: (g) => 400 + g / 10,  // tanky megafauna
  armored: true,
  bodyRadius: 1.8, // ~3m beast — large footprint, attacker stands well clear
};

/**
 * Nightwisp — pets-only ranged caster (Tavern purchase). Never ambient-spawned.
 *   poolIndex -1 so the spawner never touches it.
 *   Stats come from PET_DEFS.Nightwisp (17-pets.md §"v1 pet roster"); scaleHp/
 *   scaleDamage here are minimal stubs — owned creatures short-circuit the AI
 *   loop before these are read in combat, and pet engage reads from PET_DEFS.
 *   role "passive-flee" is irrelevant: owned-creature AI returns early before
 *   any role branch executes.
 *   meshUrl is null — Nightwisp renders as a procedural glowing orb
 *   (creature-render ORB_RENDER_TYPES / createChannelOrb), not a GLTF. A
 *   non-null placeholder path would 404 → SPA-fallback HTML → reject the whole
 *   asset preload and break world load.
 */
const NIGHTWISP: CreatureDef = {
  type: "Nightwisp",
  meshUrl: null,
  scale: 0.30,
  role: "passive-flee",
  activity: "always",
  baseSpeed: 2.25,  // matches Wolf — follow speed set by pet-follow.ts, not role
  poolIndex: -1,    // never ambient-spawned; spawner skips poolIndex < 0
  sight: 0,
  xp: 0,
  proximityFlee: false,
  spawnIntervalSec: Number.POSITIVE_INFINITY,
  spawnBurstMax: 0,
  drops: [],
  scaleHp: () => 85,      // stub — pet HP governed by PET_DEFS / recomputePetStats
  scaleDamage: () => 4,   // stub — pet dmg governed by PET_DEFS / recomputePetStats
};

export const CREATURE_DEFS: Record<CreatureType, CreatureDef> = {
  Stag: STAG, Sheep: SHEEP, Fox: FOX,
  Wolf: WOLF, Bear: BEAR, Lyote: LYOTE,
  Ghoul: GHOUL, UndeadKobold: UNDEAD_KOBOLD, Wight: WIGHT, Drekavac: DREKAVAC,
  KingFurbeastIV: KING_FURBEAST_IV, TheOldMammoth: THE_OLD_MAMMOTH,
  Nightwisp: NIGHTWISP,
};

/** Initial quota values from MapScript.galaxy:41308-41316.
    Dynamic types (Lyote, undead) get recomputed per spawn tick by the spawner;
    these are the boot values until the first recompute. Boss + landmark zero
    because they're not pool-spawned. */
export const CREATURE_QUOTAS: Record<CreatureType, number> = {
  Stag: 21,
  Sheep: 100,
  Fox: 80,                // gv_blueJunglefowlQuota
  Wolf: 40,               // gv_wolfQuota (static)
  Bear: 18,               // gv_bearQuota (static)
  Lyote: 20,              // gv_direWolfQuota initial; recomputes to 3 × num_living_kobolds
  Ghoul: 0,               // unified undeadQuota recomputes; per-type zero is OK because spawner sums
  UndeadKobold: 0,
  Wight: 0,
  Drekavac: 0,
  KingFurbeastIV: 0,      // spawner skips entries with poolIndex < 0
  TheOldMammoth: 6,      // roaming wild megafauna herd (re-theme 2026-06-23; was 0 inert landmark)
  Nightwisp: 0,          // pets-only; spawner skips (poolIndex -1)
};

/** Total undead population cap (sum across Ghoul + UndeadKobold + Wight + Drekavac).
    KTS-verbatim from MapScript.galaxy:20760:
      undeadQuota = 50 + 12 × num_living_kobolds + ghoulExtra
    ghoulExtra is the per-tier escalation bump from the 4-tier undead camp ladder
    (MapScript.galaxy:13730/13749/13776/13808). The ladder is out of scope for v1
    (spec §11); ghoulExtra locked at 0 here. */
export function computeUndeadQuotaTotal(numLivingKobolds: number, ghoulExtra: number = 0): number {
  return 50 + 12 * numLivingKobolds + ghoulExtra;
}

/** Lyote (DireWolf) quota recompute. KTS-verbatim from MapScript.galaxy:20746:
      direWolfQuota = 3 × num_living_kobolds */
export function computeLyoteQuota(numLivingKobolds: number): number {
  return 3 * numLivingKobolds;
}

export const UNDEAD_TYPES: ReadonlyArray<CreatureType> = ["Ghoul", "UndeadKobold", "Wight", "Drekavac"];
